'use client';

import type { ImageProps, StaticImageData } from 'next/image';
import Image from 'next/image';
import React, { useState } from 'react';

import { EmptyImage } from '@/assets';

const S3_URL = process.env.NEXT_PUBLIC_S3_BUCKET_URL;

export interface CustomImageProps extends Omit<ImageProps, 'src'> {
  src?: string;
  fallback?: string | StaticImageData;
  imageClassName?: string;
}

export default function CustomImage({
  src,
  fallback = EmptyImage,
  className,
  imageClassName,
  ...props
}: CustomImageProps) {
  const resolvedSrc = !src
    ? fallback
    : src.startsWith('http') || src.startsWith('data')
      ? src
      : `${S3_URL}/${src}`;

  return (
    <ResolvedImage
      key={`${String(resolvedSrc)}-${String(fallback)}`}
      image={resolvedSrc}
      fallback={fallback}
      className={className}
      imageClassName={imageClassName}
      {...props}
    />
  );
}

function ResolvedImage({
  image,
  fallback,
  className,
  imageClassName,
  ...props
}: Omit<CustomImageProps, 'src'> & {
  image: string | StaticImageData;
}) {
  const [currentImage, setCurrentImage] = useState(image);

  return (
    <div
      className={`relative overflow-hidden ${className} ${
        currentImage ? '' : 'animate-skeleton'
      }`}
    >
      {currentImage && (
        <Image
          {...props}
          src={currentImage}
          alt={props.alt}
          className={imageClassName}
          fill={true}
          draggable={false}
          priority={false}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          onError={() => {
            setCurrentImage(fallback);
          }}
        />
      )}
    </div>
  );
}
